import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Settings, ChevronLeft, Loader2, Shield, Save, Bell, 
  AlertTriangle, ToggleLeft, ToggleRight, Wrench, Home
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import adminApi from "../../lib/adminApi";

const ADMIN_EMAIL = "cpt.serdarakdemir@gmail.com";

export default function AdminSettings() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    maintenance_mode: false,
    maintenance_message: "",
    registration_enabled: true,
    features: {
      leagues: true,
      tournaments: true,
      messaging: true,
      ai_commentary: true
    }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await adminApi.getSettings();
      setSettings(response.data);
    } catch (error) {
      console.error("Settings error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.updateSettings(settings);
      toast.success("Ayarlar kaydedildi");
    } catch (error) {
      toast.error("Kaydetme başarısız");
    } finally {
      setSaving(false);
    }
  };

  const toggleFeature = (feature) => {
    setSettings({
      ...settings,
      features: {
        ...settings.features,
        [feature]: !settings.features[feature]
      }
    });
  };

  // Check admin access
  if (user?.email !== ADMIN_EMAIL) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Yetkisiz Erişim</h1>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#4DFFFF]" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-settings">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/dashboard" className="p-2 rounded-lg bg-[#4DFFFF]/20 hover:bg-[#4DFFFF]/30 text-[#4DFFFF]" title="Ana Sayfa">
            <Home className="w-5 h-5" />
          </Link>
          <Link to="/admin" className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white" title="Admin Panel">
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-3">
            <Settings className="w-6 h-6 text-[#F61225]" />
            <h1 className="text-2xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
              Sistem Ayarları
            </h1>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#4DFFFF] text-[#050E2E]">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Kaydet
        </Button>
      </div>

      <div className="space-y-6">
        {/* Maintenance Mode */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <Wrench className="w-5 h-5 text-yellow-500" />
            <h2 className="text-lg font-bold text-white">Bakım Modu</h2>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
              <div>
                <p className="text-white font-medium">Bakım Modu</p>
                <p className="text-sm text-[#64748B]">Aktif edildiğinde kullanıcılar siteye erişemez</p>
              </div>
              <button
                onClick={() => setSettings({ ...settings, maintenance_mode: !settings.maintenance_mode })}
                className={`p-2 rounded-lg transition-colors ${settings.maintenance_mode ? 'bg-yellow-500/20 text-yellow-400' : 'bg-white/5 text-[#64748B]'}`}
              >
                {settings.maintenance_mode ? <ToggleRight className="w-8 h-8" /> : <ToggleLeft className="w-8 h-8" />}
              </button>
            </div>

            {settings.maintenance_mode && (
              <div>
                <label className="text-sm text-[#B1C1C1]">Bakım Mesajı</label>
                <Input
                  value={settings.maintenance_message}
                  onChange={(e) => setSettings({ ...settings, maintenance_message: e.target.value })}
                  placeholder="Kullanıcılara gösterilecek mesaj..."
                  className="mt-1 bg-[#050E2E]/50 border-white/10 text-white"
                />
              </div>
            )}
          </div>
        </div>

        {/* Registration */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-5 h-5 text-[#4DFFFF]" />
            <h2 className="text-lg font-bold text-white">Kayıt Ayarları</h2>
          </div>
          
          <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
            <div>
              <p className="text-white font-medium">Yeni Kayıtlar</p>
              <p className="text-sm text-[#64748B]">Yeni kullanıcı kayıtlarına izin ver</p>
            </div>
            <button
              onClick={() => setSettings({ ...settings, registration_enabled: !settings.registration_enabled })}
              className={`p-2 rounded-lg transition-colors ${settings.registration_enabled ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}
            >
              {settings.registration_enabled ? <ToggleRight className="w-8 h-8" /> : <ToggleLeft className="w-8 h-8" />}
            </button>
          </div>
        </div>

        {/* Feature Toggles */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <ToggleRight className="w-5 h-5 text-[#22C55E]" />
            <h2 className="text-lg font-bold text-white">Özellik Kontrolleri</h2>
          </div>
          
          <div className="space-y-3">
            {[
              { key: "leagues", label: "Ligler", description: "Lig oluşturma ve yönetimi" },
              { key: "tournaments", label: "Turnuvalar", description: "Turnuva sistemi" },
              { key: "messaging", label: "Mesajlaşma", description: "Kullanıcılar arası mesajlaşma" },
              { key: "ai_commentary", label: "AI Yorumları", description: "Yapay zeka hafta yorumları" },
            ].map((feature) => (
              <div key={feature.key} className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                <div>
                  <p className="text-white font-medium">{feature.label}</p>
                  <p className="text-sm text-[#64748B]">{feature.description}</p>
                </div>
                <button
                  onClick={() => toggleFeature(feature.key)}
                  className={`p-2 rounded-lg transition-colors ${settings.features[feature.key] ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}
                >
                  {settings.features[feature.key] ? <ToggleRight className="w-8 h-8" /> : <ToggleLeft className="w-8 h-8" />}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Version Info */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-[#64748B]" />
            <h2 className="text-lg font-bold text-white">Sistem Bilgisi</h2>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 rounded-lg bg-white/5">
              <p className="text-xs text-[#64748B]">Versiyon</p>
              <p className="text-white font-medium">1.0.0</p>
            </div>
            <div className="p-4 rounded-lg bg-white/5">
              <p className="text-xs text-[#64748B]">Platform</p>
              <p className="text-white font-medium">Kahve LİGİ</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
